
import * as DIAG from "storm-react-diagrams";
import map from "lodash/map";
import reduce from "lodash/reduce";
import BaseNodeModel from "./BaseNodeModel";
import NoteNodeModel from "./nodes/NoteNodeModel";
import BaseLinkModel from "./BaseLinkModel";
import Utility from "../utilities/Utility";
import { Constant } from '../const';
import LocalVariablesUsedIn from "../utilities/usedIn/LocalVariableUsedIn";
import { updateLastChange } from "../utilities";

export default class BaseDiagramModel extends DIAG.DiagramModel {
    /**
     * Diagram model
     * @module BaseDiagramModel
     * @param {string} id Model ID
     */
    constructor(id) {
        super();
        this.id = id;
        this.nodePosIndex = {};
        this.posOccupancy = {};
        this.suppressBroadcast = false;
    }

    /**
     * Remove all nodes and links
     */
    clearElements() {
        const links = Object.values(this.links);
        for (var i = 0; i < links.length; i++) {
            this.removeLink(links[i]);
        }
        const nodes = Object.values(this.nodes);
        for (var i = 0; i < nodes.length; i++) {
            this.removeNode(nodes[i]);
        }
    }

    /**
     * Select all nodes and links
     */
    selectAll() {
        const links = Object.values(this.links);
        for (var i = 0; i < links.length; i++) {
            links[i].setSelected(true);
        }
        const nodes = Object.values(this.nodes);
        for (var i = 0; i < nodes.length; i++) {
            nodes[i].setSelected(true);
        }
    }

    /**
     * Add multiple links in an array
     * @param {Array} links Array of links to be added to the model
     */
    addLinks(links) {
        for (var i = 0; i < links.length; i++) {
            this.addLink(links[i]);
        }
    }

    /**
     * Add multiple nodes in an array
     * @param {Array} nodes Array of nodes to be added to the model
     */
    addNodes(nodes) {
        for (var i = 0; i < nodes.length; i++) {
            this.addNode(nodes[i]);
        }
    }

    /**
     * Create new version for common module instances
     * @param {array} mods 
     * @param {integer} newVersionIndex 
     */
    // CM TODO: module versions and flowVersion: remove or refactor 
    createNewVersionForCommonModules(mods, newVersionIndex){
        mods.forEach(mod => {
            const defaultVersion = _.cloneDeep(mod.versions[1]); //detached copy of `versions[1]`;
            
            mod.versions[newVersionIndex] = defaultVersion; 
            mod.versions[newVersionIndex].version = newVersionIndex;
            

        });
    }    

    /**
	 * Add a single node
	 * @param {Object} node Node model
	 * @returns {NodeModel}
	 */
	addNode(node, config = {}) {
		const isDrop = config.isDrop || false;
		const isValidation = config.isValidation || false;
		const graphModel = config.graphModel || null; // containing module
        const modelVersion = config.version || 1;
        const isCommonModuleNode =
			node.props.isCommonModule &&
			node.props.isRootModule &&
            node.props.nodeTypeName === "Module";
        

		// Update node position cache
		const newPos = node.x + "," + node.y
		if (this.nodePosIndex.hasOwnProperty(node.getID())) {
			const oldPos = this.nodePosIndex[node.getID()]
			delete this.posOccupancy[oldPos]
		}
		this.nodePosIndex[node.getID()] = newPos
		this.posOccupancy[newPos] = true

		if (isDrop) {
			if (isCommonModuleNode && Constant.k.SUPPORT_FLOW_VERSIONS) {
                // CM TODO: module versions and flowVersion: remove or refactor use of flowVersion and createNewVersionForCommonModules
                const mod = window.apm.serialized.components.modules.find(
                    mod => mod.props.moduleId === node.props.moduleId
                );
                const newVersionIndex = parseInt(Object.keys(mod.versions)[Object.keys(mod.versions).length - 1], 10) + 1;
                const dependencyModules = window.apm.serialized.components.modules.filter(m => m.name.match(mod.name + "::"));
                const mods = [mod, ...dependencyModules];
                this.createNewVersionForCommonModules(mods, newVersionIndex)
            }
                
			node.props.flowVersion = 1; // default

		} 
		else {
            if(!isValidation){
                // TODO: flowVersion is never modified, this block can be considered unused. Test before removing
                // CM TODO: module versions and flowVersion: remove or refactor use of flowVersion
                if(graphModel){
                    if (graphModel.props.isCommonModule) {
                        // every node's flow version must match the parent common module instance version
                        node.props.flowVersion = modelVersion
                    } else {
                        // find node's flow version in graphModel's versioned nodes list
                        node.props.flowVersion = graphModel.versions[modelVersion].flow.nodes.find(n => n.id === node.id).props.flowVersion || 1;
                    }
                }
            }
            
		}

		const superResult = super.addNode(node)
		if (!this.suppressBroadcast) {
			window.apm.postEventToCurrentView({
				target: "request-update-member",
				type: "add",
				node: node,
				hidePropertyPanel: true,
			})
			window.apm.postEventToCurrentView({ target: "request-refresh" })
		}
		return superResult
	}

    /**
     * Remove a single node
     * @param {Object} node Node model to be removed
     */
    removeNode(node) {
        if (!node instanceof DIAG.NodeModel) {
            node = this.nodes["" + node];
        }
        const pos = this.nodePosIndex[node.getID()];
        delete this.posOccupancy[pos];
        delete this.nodePosIndex[node.getID()];
        if (node.props.nodeTypeName === "Module Begin") {
            return;
        }
        // Update node position cache
        else {
            super.removeNode(node);
            if (!this.suppressBroadcast) {
                window.apm.postEventToCurrentView({
                    target: "request-update-member",
                    type: "remove",
                    node: node
                });

                window.apm.postEventToCurrentView({ target: "request-refresh"});
            }
        }
    }
    /**
     * Update current diagram member treeview
     * @param {Object} treeDom jQuery reference to the treeview
     * @param {string} type Action type
     * @param {Object} node A single node model the action will use
     */
    updateMemberTree(treeDom, type, node) {
        if (this.suppressBroadcast) {
            return;
        }
        if (type === "add") { // add a single node
            if (node instanceof NoteNodeModel) {
                return;
            }
            if (node.type === "object") {
                if (!treeDom.jstree().get_node(node.props.nodeTypeName)) {
                    // add type name folder if not exist
                    treeDom.jstree().create_node("objects", {
                        "id": node.props.nodeTypeName,
                        "text": node.props.nodeTypeName,
                        "type": "objects",
                        "state": {
                            "opened": true
                        },
                        "children": [Utility.getTreeNode(node)]
                    }, "last");
                } else {
                    treeDom.jstree().create_node(node.props.nodeTypeName, Utility.getTreeNode(node), "last");
                }
            } else {
                treeDom.jstree().create_node(node.type + "s", Utility.getTreeNode(node), "last");
            }
            return;
        }

        if (type === "remove") { // delete a single node
            if (node.props.nodeTypeName !== "Module Begin") {
                treeDom.jstree().delete_node(node.id);
            }
            // remove empty type name folder
            if (node.type === "object" && node.props.nodeTypeName !== "Module Begin") {


                if (treeDom.jstree().get_node(node.props.nodeTypeName).children && treeDom.jstree().get_node(node.props.nodeTypeName).children.length === 0) {
                    treeDom.jstree().delete_node(node.props.nodeTypeName);
                }
            }
            return;
        }

        // Update entire tree
        const diagramWidget = window.apm.getCurrentDiagramWidget();
        const treeMembers = {
            "currentMod": [],
            "variables": [],
            "modules": [],
            "objects": []
        };
        // DEVDES2-1369 - Allow user to view current module's properties while within its tab
        if(diagramWidget.props.node.id !== "rootModule"){
            treeMembers.currentMod.push(Utility.getTreeNode(diagramWidget.props.node));
        }

        const localVariables = diagramWidget.getLocalVariables();
        if (localVariables) {
            const mappedLocalVariables = localVariables.map((localVariable) => {
                localVariable.props.iconClass = localVariable.props.config?.moduleInput ? "corner-down-right" : "hash";
                return Utility.getTreeNode(localVariable);
            });
            treeMembers.variables = treeMembers.variables.concat(mappedLocalVariables);
        }

        const nodes = Object.values(this.nodes);
        const objTypes = {};
        for (var i = 0; i < nodes.length; i++) {
            var isDuplicate = false;
            const node = nodes[i];
            if (node.props.parentComponentID) { }
            if (node instanceof NoteNodeModel) {
                continue;
            }
            if (node.type === "object") {
                if (!objTypes.hasOwnProperty(node.props.nodeTypeName) && node.props.nodeTypeName !== "Module Begin") {
                    objTypes[node.props.nodeTypeName] = [];

                }
                if (node.props.parentComponentID) {
                    var existingModel = objTypes[node.props.nodeTypeName].filter(newNode => newNode.props.parentComponentID === node.props.parentComponentID);
                    if (existingModel.length > 0) {
                        isDuplicate = true;
                    }
                    if (!isDuplicate) {
                        const leaf = Utility.getTreeNode(node);
                        objTypes[node.props.nodeTypeName].push(leaf);
                    }
                }
            } else {

                treeMembers[node.type + "s"].push(Utility.getTreeNode(node));
            }
        }
        for (var objType in objTypes) {
            if (objTypes.hasOwnProperty(objType)) {
                treeMembers["objects"].push({
                    "id": objType,
                    "text": objType,
                    "type": "objects",
                    "state": {
                        "opened": true
                    },
                    "children": objTypes[objType]
                });
            }
        }

        const coreData = [{
            "id": "currentMod",
            "text": "Current Module",
            "type": "currentMod",
            "state": {
                "opened": true
            },
            "children": treeMembers.currentMod
        },
        {
            "id": "variables",
            "text": window.l.library.local_variables,
            "type": "variables",
            "state": {
                "opened": true
            },
            "children": treeMembers.variables
        },
        {
            "id": "modules",
            "text": window.l.library.modules,
            "type": "modules",
            "state": {
                "opened": true
            },
            "children": treeMembers.modules
        },
        {
            "id": "objects",
            "text": window.l.library.objects,
            "type": "objects",
            "state": {
                "opened": true
            },
            "children": treeMembers.objects
        }
        ];

        var treeObj = treeDom.jstree();
        if (treeObj) {
            treeObj.settings.core.data = coreData;
            treeObj.refresh();
        } else {
            // Solve initial load race condition, and remove updating from UI cycle
            clearInterval(this.updateTimer);
            this.updateTimer = setInterval(() => {
                treeObj = treeDom.jstree();
                if (treeObj) {
                    clearInterval(this.updateTimer);
                    treeObj.settings.core.data = coreData;
                    treeObj.refresh();
                }
            }, 200);
        }
    }

    /**
     * Update node position cache
     * @param {BaseNodeModel} node Node model
     */
    updateNodePos(node) {
        const newPos = node.x + "," + node.y;
        if (this.nodePosIndex.hasOwnProperty(node.getID())) {
            const oldPos = this.nodePosIndex[node.getID()];
            delete this.posOccupancy[oldPos];
        }
        updateLastChange(node.props);
        Object.values(node.ports).forEach((port) => {
            Object.values(port.links).forEach((link) => {
                updateLastChange(link.props);
            });
        });
        this.nodePosIndex[node.getID()] = newPos;
        this.posOccupancy[newPos] = true;
    }

    /**
     * Is node overlapping another pre-existing node?
     * @param {Object} node Node model
     * @returns {boolean}
     */
    nodeHasOverlap(node) {
        const pos = node.x + "," + node.y;
        return this.posOccupancy.hasOwnProperty(pos) && this.posOccupancy[pos] && this.nodePosIndex[node.getID()] !== pos;
    }

    /**
     * Is the position overlapping another pre-existing node's position?
     * @param {Object} mousePos Mouse position
     * @returns {boolean}
     */
    posHasOverlap(mousePos) {
        const pos = mousePos.x + "," + mousePos.y;
        return this.posOccupancy.hasOwnProperty(pos) && this.posOccupancy[pos];
    }

    /**
     * Walk the graph and evaluate edges based on pre-defined configuration values of the module
     * @param staticInputs
     */
    configEvaluation(staticInputs) {
        // Find begin node
        const nodes = Object.values(this.nodes);
        const beginNodes = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i].constructor.name === "BeginNodeModel") {
                beginNodes.push(nodes[i]);
            }
        }
        // Valid graph only has one begin node
        if (beginNodes.length !== 1) {
            document.dispatchEvent(new CustomEvent("ev-graph-full-graph"));
            return;
        }
        // DFS
        var visited = {
            nodes: {},
            links: {}
        };
        beginNodes[0].dfs(visited, {
            type: "configEvaluation",
            staticInputs: staticInputs
        });
    }

    /**
     * Validate entire diagram model
     * @returns {Object}
     */
    validateDiagram(showError = false) {
        const errors = {
            errorCount: 0,
            beginNode: [],
            inputNode: [],
            dataNode: [],
            exitNode: [],
            moduleReferenceNode: [],
            promptReferenceNode: [],
            reportNode: [],
            promptNode: [],
            playSavedPrompt: [],
            savePrompt: [],
            links: [],
            nodes: [],
            unusedLinks: [],
            unusedNodes: [],
            unusedLocalVariables: [],
            illegalObjects: [],

        };
        // validate session start module

        if (this.id === window.apm.serialized.globalErrorHandling.sessionStart) {
            Object.values(this.nodes).forEach(no => {

                if (no.props.nodeTypeName !== "Module" && no.props.nodeTypeName !== "Module Begin" && no.props.nodeTypeName !== "Tabular Router" && no.props.nodeTypeName !== "Custom Script" && no.props.nodeTypeName !== "Data Access" && no.props.nodeTypeName !== "Module Exit") {

                    errors.illegalObjects.push({
                        elem: no,
                        error: no.props.nodeTypeName + " should not exist in the Start Session module"
                    });
                    errors.errorCount++;
                }
            });
        }
        // validate links
        const links = Object.values(this.links);
        for (var i = 0; i < links.length; i++) {
            if (links[i].hasError()) {
                errors.links.push({
                    elem: links[i],
                    error: links[i].getError()
                });
                errors.errorCount++;
            }
            if (links[i].getSourceNode().props.nodeTypeName === "Module Exit") {
                this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR] = "Module Exit has as outcoming transition";
                errors.exitNode.push({
                    elem: links[i].getSourceNode(),
                    error: "Module Exit has as outcoming transition"
                });
                errors.errorCount++;
            }
            if (links[i].getConfig("condition")) {
                var condition = links[i].getConfig("condition")
                for (var c = 0; c < condition.length; c++) {
                    condition[c].forEach(co => {
                        if (co.includes("app.")) {
                            const variable = co.substring(co.indexOf("app."), co.length);
                            const sv = variable.split(".");

                            var v = null;
                            if (sv[1].includes(" ")) {
                                sv[1] = sv[1].substring(0, sv[1].indexOf(" ")).trim();
                            }
                            if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                                if (sv[1].match(new RegExp ("<|>|!=|!==|===|=="))) {
                                    sv[1] = sv[1].substring(0, sv[1].match(new RegExp ("<|>|!=|!==|===|=="))['index']);
                                }
                                v = window.apm.getVariable(sv[1]);
                            }
                            if (v === null) {
                                this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR] = "Unknown variable";
                                if (errors.links.findIndex(li => li.elem.id === links[i].id) === -1) {
                                    errors.links.push({
                                        elem: links[i],
                                        error: "Unknown variable"
                                    });
                                    errors.errorCount++;
                                }
                            }
                            else if (links[i].props.hasOwnProperty("error") && links[i].props.error[Constant.k.VALIDATION_ERROR]) {
                                delete this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR];
                            }
                        }
                        if (co.includes("mod.")) {
                            const variable = co.substring(co.indexOf("mod."), co.length);
                            const sv = variable.split(".");

                            var v = null;
                            if (sv[1].includes(" ")) {
                                sv[1] = sv[1].substring(0, sv[1].indexOf(" ")).trim();
                            }
                            if (sv[0] === Constant.k.SCOPE_MODULE) {
                                if (sv[1].match(new RegExp ("<|>|!=|!==|===|=="))) {
                                    sv[1] = sv[1].substring(0, sv[1].match(new RegExp ("<|>|!=|!==|===|=="))['index']);
                                }
                                v = window.apm.getVariable(sv[1], this.id);
                            }
                            if (v === null) {
                                this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR] = "Unknown variable";
                                if (errors.links.findIndex(li => li.elem.id === links[i].id) === -1) {
                                    errors.links.push({
                                        elem: links[i],
                                        error: "Unknown variable"
                                    });
                                    errors.errorCount++;
                                }
                            }
                            else if (links[i].props.hasOwnProperty("error") && links[i].props.error[Constant.k.VALIDATION_ERROR]) {
                                delete this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR];
                            }
                        }
                        else if ((errors.links.findIndex(li => li.elem.id === links[i].id) === -1) && links[i].props.hasOwnProperty("error") && links[i].props.error[Constant.k.VALIDATION_ERROR]) {
                            delete this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR];
                        }
                    })
                }
            }
            else if (links[i].props.hasOwnProperty("error") && links[i].props.error[Constant.k.VALIDATION_ERROR]) {
                delete this.links[links[i].id].props.error[Constant.k.VALIDATION_ERROR];
            }
        }

        // validate nodes
        const nodes = Object.values(this.nodes);
        const beginNodes = [];
        const inputNodes = [];
        const dataNodes = [];
        const moduleReferenceNodes = [];
        const promptReferenceNodes = [];
        const reportNodes = [];
        const promptNodes = [];
        const savePrompts = [];
        const playSavedPrompts = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i].hasError() && !nodes[i].props.error[Constant.k.VALIDATION_ERROR]) {
                errors.nodes.push({
                    elem: nodes[i],
                    error: nodes[i].getError()
                });
                errors.errorCount++;
            }
            if (nodes[i].constructor.name === "BeginNodeModel") {
                beginNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "InputNodeModel") {
                inputNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "DataNodeModel") {
                dataNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "ModuleReferenceNodeModel") {
                moduleReferenceNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "PromptReferenceNodeModel") {
                promptReferenceNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "ReportNodeModel") {
                reportNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "PromptNodeModel") {
                promptNodes.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "SavePromptNodeModel") {
                savePrompts.push(nodes[i]);
            }
            if (nodes[i].constructor.name === "PlaySavedPromptNodeModel") {
                playSavedPrompts.push(nodes[i]);
            }
        }
        //check data access node
        if (dataNodes.length > 0) {
            dataNodes.forEach(dn => {
                if (!dn.props.config.schema) {
                    this.nodes[dn.id].props.error[Constant.k.VALIDATION_ERROR] = "No schema";
                    errors.dataNode.push({
                        elem: dn,
                        error: "No schema"
                    });
                    errors.errorCount++;
                }
                else if (dn.props.hasOwnProperty("error") && dn.props.error[Constant.k.VALIDATION_ERROR]) {
                    delete this.nodes[dn.id].props.error[Constant.k.VALIDATION_ERROR];
                }
                if (showError) window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: dn.id, type: "node", upDatedField: "error", error: "error", node: dn, props: dn.props } });
            })
        }
        //check prompt Nodes
        if (promptNodes.length > 0) {
            promptNodes.forEach(pr => {
                if (pr.props.config.text) {
                    var keys = Object.keys(pr.props.config.text);
                    var index1, index2 = -1
                    for (var k = 0; k < keys.length; k++) {
                        var text = pr.props.config.text[keys[k]]
                        if (text.includes("{{") && text.match(new RegExp("{{", "g")).length > 0) {
                            for (var r = 0; r < text.match(new RegExp("{{", "g")).length; r++) {
                                index1 = text.indexOf("{{");
                                index2 = text.indexOf("}}");
                                const sv = text.substring(index1 + 2, index2).split(".");
                                var v = null;

                                if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                                    v = window.apm.getVariable(sv[1]);
                                }
                                if (sv[0] === Constant.k.SCOPE_MODULE) {
                                    v = window.apm.getVariable(sv[1], this.id);
                                }
                                if (v === null) {
                                    var prIndex = errors.promptNode.findIndex(el => el.elem.id === pr.id);
                                    if (prIndex === -1) {
                                        this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR] = "Unknown variable";
                                        errors.promptNode.push({
                                            elem: pr,
                                            error: "Unknown variable"
                                        });
                                        errors.errorCount++;
                                    }

                                }
                                else if (pr.props.hasOwnProperty("error") && pr.props.error[Constant.k.VALIDATION_ERROR]) {
                                    delete this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR];
                                }

                            }
                        }
                        else if (pr.props.hasOwnProperty("error") && pr.props.error[Constant.k.VALIDATION_ERROR]) {
                            delete this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR];
                        }
                    }

                }
                if (showError) window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: pr.id, type: "node", upDatedField: "error", error: "error", node: pr, props: pr.props } });

            });
        }
        //check savePormpts
        if (savePrompts.length > 0) {
            savePrompts.forEach(sp => {
                if (sp.props.config && (sp.props.config.playSavedPromptId === undefined || sp.props.config.playSavedPromptId === "")) {
                    this.nodes[sp.id].props.error[Constant.k.VALIDATION_ERROR] = " Key cannot be empty";
                    errors.savePrompt.push({
                        elem: sp,
                        error: " Key cannot be empty"
                    });
                    errors.errorCount++;
                }
                else if (sp.props.hasOwnProperty("error") && sp.props.error[Constant.k.VALIDATION_ERROR]) {
                    delete this.nodes[sp.id].props.error[Constant.k.VALIDATION_ERROR];
                }
            });

        }
        //check report nodes
        if (reportNodes.length > 0) {
            reportNodes.forEach(rp => {
                for (var key in rp.props.config) {
                    var sv;
                    switch (key) {
                        case "transferReason":
                        case "transferDestination":
                        case "scriptProgress":
                            sv = rp.props.config[key].split(".");
                            var v = null;
                            if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                                v = window.apm.getVariable(sv[1]);
                            }
                            if (sv[0] === Constant.k.SCOPE_MODULE) {
                                v = window.apm.getVariable(sv[1], this.id);
                            }
                            if (sv.length > 1 && v === null) {
                                this.nodes[rp.id].props.error[Constant.k.VALIDATION_ERROR] = key + " : Unknown variable";
                                errors.reportNode.push({
                                    elem: rp,
                                    error: key + " : Unknown variable"
                                });
                                errors.errorCount++;
                            }
                            else if (rp.props.hasOwnProperty("error") && rp.props.error[Constant.k.VALIDATION_ERROR]) {
                                delete this.nodes[rp.id].props.error[Constant.k.VALIDATION_ERROR];
                            };
                            break;
                        case "billing":
                        case "customTag":
                            if (rp.props.config[key] && rp.props.config[key].value !== undefined) {
                                sv = rp.props.config[key].value.split(".");
                                var v = null;

                                if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                                    v = window.apm.getVariable(sv[1]);
                                }
                                if (sv[0] === Constant.k.SCOPE_MODULE) {
                                    v = window.apm.getVariable(sv[1], this.id);
                                }
                                if (sv.length > 1 && (sv[0] === Constant.k.SCOPE_APPLICATION || sv[0] === Constant.k.SCOPE_MODULE) && v === null) {
                                    this.nodes[rp.id].props.error[Constant.k.VALIDATION_ERROR] = key + " : Unknown variable";
                                    errors.reportNode.push({
                                        elem: rp,
                                        error: key + " : Unknown variable"
                                    });
                                    errors.errorCount++;
                                }
                                else if (rp.props.hasOwnProperty("error") && rp.props.error[Constant.k.VALIDATION_ERROR]) {
                                    delete this.nodes[rp.id].props.error[Constant.k.VALIDATION_ERROR];
                                }
                            }

                    }
                }
                if (showError) window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: rp.id, type: "node", upDatedField: "error", error: "error", node: rp, props: rp.props } });
            })
        }
        //check input node
        if (inputNodes.length > 0) {
            var intents = window.apm.knowledgeBase.getIntents();
            for (var input = 0; input < inputNodes.length; input++) {
                var localIntents = [];
                var sourcePorts = [];
                let deleteLocalIntents = [];
                if (inputNodes[input].props.config.localIntents && inputNodes[input].props.config.localIntents.length > 0) {
                    for (var i = 0; i < links.length; i++) {
                        if (links[i].getSourceNode().id === inputNodes[input].id) {
                            sourcePorts.push(links[i]);
                        }
                    }
                    for (var li = 0; li < inputNodes[input].props.config.localIntents.length; li++) {
                        var intentIndex = intents.findIndex(l => l.value === inputNodes[input].props.config.localIntents[li]);
                        if (intentIndex !== -1) {
                            sourcePorts.forEach(sp => {
                                if (sp.getConfig("condition") && JSON.stringify(sp.getConfig("condition")).includes(intents[intentIndex].name)) {
                                    var loIndex = localIntents.findIndex(lo => lo === inputNodes[input].props.config.localIntents[li])
                                    if (loIndex === -1) { localIntents.push(inputNodes[input].props.config.localIntents[li]); }
                                }
                            });
                        }
                        if (intentIndex === -1) {
                            deleteLocalIntents.push(inputNodes[input].props.config.localIntents[li]);
                         }
                    } 
                    inputNodes[input].props.config.localIntents = inputNodes[input].props.config.localIntents.filter(li =>deleteLocalIntents.findIndex(i => i === li) === -1);
                    if (inputNodes[input].props.config.localIntents.length !== localIntents.length) {
                        this.nodes[inputNodes[input].id].props.error[Constant.k.VALIDATION_ERROR] = "Unused Local Intent";
                        errors.inputNode.push({
                            elem: inputNodes[input],
                            error: "Unused Local Intent"
                        });
                        errors.errorCount++;
                    }
                    else if (inputNodes[input].props.hasOwnProperty("error") && inputNodes[input].props.error[Constant.k.VALIDATION_ERROR]) {
                        delete this.nodes[inputNodes[input].id].props.error[Constant.k.VALIDATION_ERROR];
                    }
                }
                else if (inputNodes[input].props.hasOwnProperty("error") && inputNodes[input].props.error[Constant.k.VALIDATION_ERROR]) {
                    delete this.nodes[inputNodes[input].id].props.error[Constant.k.VALIDATION_ERROR];
                }
                if (showError) window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: inputNodes[input].id, type: "node", upDatedField: "error", error: "error", node: inputNodes[input], props: inputNodes[input].props } });
            }
        }
        //check prompt reference node
        // TODO: inspect this section for promptReferenceNodes promptReference usage, etc
        if (promptReferenceNodes.length > 0) {
            promptReferenceNodes.forEach(pr => {
                if (pr.props.config.promptReference && pr.props.config.promptReference !== "") {
                    pr.props.validatorError = false;

                    const sv = pr.props.config.promptReference.split(".");
                    var v = null;
                    if (sv.length === 1) {
                        this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR] = "Prompt Reference is not a variable";
                        errors.promptReferenceNode.push({
                            elem: pr,
                            error: "Prompt Reference is not a variable"
                        });
                        errors.errorCount++;
                    }
                    else if (sv.length > 1) {
                        if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                            v = window.apm.getVariable(sv[1]);
                        }
                        if (sv[0] === Constant.k.SCOPE_MODULE) {
                            v = window.apm.getVariable(sv[1], this.id);
                        }
                        if (sv.length > 1 && v === null) {
                            this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR] = "Unknown variable";
                            errors.promptReferenceNode.push({
                                elem: pr,
                                error: "Unknown variable"
                            });
                            errors.errorCount++;
                        }
                        if (sv.length > 1 && v !== null && pr.props.hasOwnProperty("error") && pr.props.error[Constant.k.VALIDATION_ERROR]) {
                            delete pr.props.error[Constant.k.VALIDATION_ERROR];
                        }
                    }
                    else if (pr.props.hasOwnProperty("error") && pr.props.error[Constant.k.VALIDATION_ERROR]) {
                        delete pr.props.error[Constant.k.VALIDATION_ERROR];
                    }
                }
                if (showError) window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: pr.id, type: "node", upDatedField: "error", error: "error", node: pr, props: pr.props } });

            });
        }
        // check module reference node
        if (moduleReferenceNodes.length > 0) {
            moduleReferenceNodes.forEach(pr => {
                if (pr.props.config.moduleReference && pr.props.config.moduleReference !== "") {
                    const sv = pr.props.config.moduleReference.split(".");
                    var v = null;
                    if (sv.length === 1) {
                        this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR] = "Module Reference is not a variable";
                        errors.moduleReferenceNode.push({ elem: pr, error: "Module Reference is not a variable" });
                        errors.errorCount++;
                    }
                    else if (sv.length > 1) {
                        if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                            v = window.apm.getVariable(sv[1]);
                        }
                        if (sv[0] === Constant.k.SCOPE_MODULE) {
                            v = window.apm.getVariable(sv[1], this.id);
                        }
                        if (sv.length > 1 && v === null) {
                            this.nodes[pr.id].props.error[Constant.k.VALIDATION_ERROR] = "Module Reference is not a variable";
                            errors.moduleReferenceNode.push({ elem: pr, error: "Unknown variable" });
                            errors.errorCount++;
                        }
                        if (sv.length > 1 && v !== null && pr.props.hasOwnProperty("error") && pr.props.error[Constant.k.VALIDATION_ERROR]) {
                            delete pr.props.error[Constant.k.VALIDATION_ERROR];
                        }
                    }
                    else if (pr.props.hasOwnProperty("error") && pr.props.error[Constant.k.VALIDATION_ERROR]) {
                        delete pr.props.error[Constant.k.VALIDATION_ERROR];
                    }
                }
                if (showError) window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: pr.id, type: "node", upDatedField: "error", error: "error", node: pr, props: pr.props } });

            });
        }

        // check begin node
        if (beginNodes.length === 0) {
            errors.beginNode.push({
                elem: null,
                error: "No begin node"
            });
            errors.errorCount++;
        } else if (beginNodes.length > 1) {
            for (var i = 0; i < beginNodes.length; i++) {
                errors.beginNode.push({
                    elem: beginNodes[i],
                    error: "Begin node is not unique"
                });
                errors.errorCount++;
            }
        } else {
            // graph bfs
            const visited = [];
            this.bfs(beginNodes[0], visited);

            // check unused
            for (var i = 0; i < links.length; i++) {
                if (visited.indexOf(links[i].id) === -1) {
                    errors.unusedLinks.push({
                        elem: links[i],
                        error: "Transition is not reachable"
                    });
                    errors.errorCount++;
                }
            }
            for (var i = 0; i < nodes.length; i++) {
                if (visited.indexOf(nodes[i].id) === -1 && nodes[i].constructor.name !== "NoteNodeModel") {
                    errors.unusedNodes.push({
                        elem: nodes[i],
                        error: "Node is not reachable"
                    });
                    errors.errorCount++;
                }
            }
        }

        // check unused local variables
        const module = window.apm.getNode(this.id);
        if (module.localVariables) {
           const unusedVariables= LocalVariablesUsedIn.getUnusedVariables(module.localVariables,module, window.apm.serialized);
            if (unusedVariables.length > 0) {
                unusedVariables.forEach(lv => {
                    const varIndex = module.localVariables.findIndex(v => v.id === lv);
                    if (varIndex !== -1) {
                        errors.unusedLocalVariables.push({
                        elem: module.localVariables[varIndex],
                        error: "Local variable is unused"
                    });
                    errors.errorCount++;}
                })
                    
                }
            }
        

        return errors;
    }

    /**
     * Breath first search
     * @param {Object} element Model element
     * @param {Object} visited Search frontier
     */
    bfs(element, visited) {
        if (visited.indexOf(element.id) !== -1) {
            return;
        }
        visited.push(element.id);
        if (element instanceof BaseNodeModel) {
            const outLinks = element.getOutLinks();
            for (var i = 0; i < outLinks.length; i++) {
                this.bfs(outLinks[i], visited);
            }
        }
        if (element instanceof BaseLinkModel) {
            const target = element.getTargetNode();
            if (target) {
                this.bfs(target, visited);
            }
        }
    }

    /**
     * Serialize diagram model for storage
     * @returns {Object}
     */
    serializeDiagram() {
        var s = {
            offsetX: this.offsetX,
            offsetY: this.offsetY,
            zoom: this.zoom,
            links: reduce(this.links, function (links, link) {
                const l = link.serialize();
                if (l.source) {
                    links.push(l);
                }
                return links;
            }, []),
            nodes: map(this.nodes, node => {
                return node.serialize();
            })
        };
        return s;
    }
}
